/**
 * Environment Configuration
 * 
 * Centralized configuration for API endpoints and environment variables.
 * This ensures type safety and makes it easy to switch between environments.
 */

/**
 * Application environment types
 */
export type Environment = 'development' | 'production' | 'test';

/**
 * Environment configuration interface
 */
export interface EnvironmentConfig {
  /** Current environment */
  environment: Environment;
  /** Base API URL */
  apiBaseUrl: string;
  /** API version */
  apiVersion?: string;
  /** Whether to enable debug logging */
  enableDebugLogging: boolean;
}

/**
 * Get the current environment
 */
const getEnvironment = (): Environment => {
  const env = process.env.NODE_ENV || 'development';
  return env as Environment;
};

/**
 * Get API base URL from environment variables or use default
 */
const getApiBaseUrl = (): string => {
  // Check for environment variable first
  if (process.env.REACT_APP_API_BASE_URL) {
    // Trim trailing slash if present
    return process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, '');
  }
  
  // Fallback to default based on environment
  const env = getEnvironment();
  if (env === 'production') {
    // Production: use same-origin (empty string for relative URLs)
    return '';
  }
  
  // Development default
  return 'http://localhost:30051';
};

/**
 * Application configuration
 */
export const config: EnvironmentConfig = {
  environment: getEnvironment(),
  apiBaseUrl: getApiBaseUrl(),
  apiVersion: process.env.REACT_APP_API_VERSION || 'v1',
  enableDebugLogging: getEnvironment() === 'development',
};

/**
 * API endpoint paths
 * All endpoints use /api/* prefix for Cloudflare Pages Functions
 */
export const apiEndpoints = {
  /** Public blog endpoints */
  blogs: {
    /** Get list of blog posts */
    list: '/api/blogs',
    /** Get single blog post by ID */
    byId: (id: string) => `/api/blogs/${encodeURIComponent(id)}`,
    /** Get single blog post by slug */
    bySlug: (slug: string) => `/api/blogs/${encodeURIComponent(slug)}`,
    /** Like a blog post */
    like: (id: string) => `/api/blogs/${encodeURIComponent(id)}/like`,
    /** Share a blog post */
    share: (id: string) => `/api/blogs/${encodeURIComponent(id)}/share`,
    /** Get blog metrics */
    metrics: (id: string) => `/api/blogs/${encodeURIComponent(id)}/metrics`,
  },
  /** Admin endpoints */
  admin: {
    /** 
     * Admin authentication endpoints (deprecated)
     * @deprecated Authentication is now handled by Cloudflare Access at the edge
     */
    auth: {
      login: '/api/admin/auth/login',
      logout: '/api/admin/auth/logout',
      verify: '/api/admin/auth/verify',
    },
    /** Admin authentication check */
    me: '/api/admin/me',
    /** Admin blog management */
    blogs: {
      /** Get list of all blog posts (including drafts) */
      list: '/api/admin/blogs',
      /** Create a new blog post */
      create: '/api/admin/blogs',
      /** Update an existing blog post */
      update: (id: string) => `/api/admin/blogs/${encodeURIComponent(id)}`,
      /** Delete a blog post */
      delete: (id: string) => `/api/admin/blogs/${encodeURIComponent(id)}`,
      /** Get a blog post by ID for editing */
      getById: (id: string) => `/api/admin/blogs/${encodeURIComponent(id)}`,
      /** Publish a blog post */
      publish: (id: string) => `/api/admin/blogs/${encodeURIComponent(id)}/publish`,
    },
  },
} as const;

/**
 * Helper function to build full API URL
 */
export const buildApiUrl = (endpoint: string): string => {
  // If base URL is empty (same-origin), return the endpoint path
  if (config.apiBaseUrl === '') {
    // Ensure endpoint starts with /
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }
  
  // Normalize base URL by trimming trailing slash
  const baseUrl = config.apiBaseUrl.replace(/\/+$/, '');
  // Ensure endpoint starts with /
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${path}`;
};

